import { useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { api } from "@/lib/api";
import { ADDRESS_LINE_1, ADDRESS_LINE_2, PHONE, PHONE_TEL, EMAIL, INSTAGRAM_URL } from "@/data/content";
import BookingButton from "./BookingButton";

function buildContactMailto(c) {
  const subject = c.subject?.trim()
    ? `Sano Acupuncture — ${c.subject}`
    : `Sano Acupuncture — message from ${c.name || "visitor"}`;
  const lines = [
    `Hi Sano Acupuncture,`,
    ``,
    c.message,
    ``,
    `— ${c.name}`,
    c.email ? `Email: ${c.email}` : null,
    c.phone ? `Phone: ${c.phone}` : null,
  ].filter(Boolean);
  return `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
}

export default function ContactNewsletter() {
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "", phone: "" });
  const [nlEmail, setNlEmail] = useState("");
  const [loadingC, setLoadingC] = useState(false);
  const [loadingN, setLoadingN] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setLoadingC(true);
    try {
      await api.post("/contact", contact);
      // Open the visitor's email client pre-filled to sanoacu@gmail.com
      window.location.href = buildContactMailto(contact);
      toast.success("Saved. Opening your email app to finish sending…");
      setContact({ name: "", email: "", subject: "", message: "", phone: "" });
    } catch {
      toast.error(`Could not send. Email ${EMAIL} directly.`);
    } finally {
      setLoadingC(false);
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!nlEmail) return;
    setLoadingN(true);
    try {
      await api.post("/newsletter", { email: nlEmail });
      toast.success("Welcome. A gentle letter, once a month.");
      setNlEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoadingN(false);
    }
  };

  return (
    <section id="contact" className="py-28 lg:py-36" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-4">09 · Stay in touch</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-6">
            A letter, once a moon.
          </h2>
          <p className="text-base leading-relaxed mb-8" style={{ color: "var(--sano-ink-2)" }}>
            Seasonal recipes, acupressure protocols, upcoming workshops. No spam,
            no urgency — just the kind of email you&apos;d actually open.
          </p>

          <form onSubmit={submitNewsletter} className="flex gap-2 mb-12" data-testid="newsletter-form">
            <input
              type="email"
              placeholder="your@email.com"
              value={nlEmail}
              onChange={(e) => setNlEmail(e.target.value)}
              className="flex-1 px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm rounded-full"
              style={{ borderColor: "var(--sano-border)" }}
              data-testid="newsletter-input"
              required
            />
            <button
              type="submit"
              disabled={loadingN}
              className="px-6 py-3 rounded-full text-sm font-medium text-[#F9F6F0] disabled:opacity-50"
              style={{ background: "var(--sano-olive)" }}
              data-testid="newsletter-submit"
            >
              {loadingN ? "…" : "Subscribe"}
            </button>
          </form>

          <div className="border-t pt-8 space-y-3 text-sm" style={{ borderColor: "var(--sano-border)", color: "var(--sano-ink-2)" }}>
            <div><span className="eyebrow block mb-1">Studio</span>{ADDRESS_LINE_1}<br/>{ADDRESS_LINE_2}</div>
            <div><span className="eyebrow block mb-1">Phone</span><a href={PHONE_TEL} className="underline" data-testid="contact-direct-phone">{PHONE}</a></div>
            <div><span className="eyebrow block mb-1">Hours</span>Tue–Sat · 9:00 am – 7:00 pm</div>
            <div><span className="eyebrow block mb-1">Email</span><a href={`mailto:${EMAIL}`} className="underline" data-testid="contact-direct-email">{EMAIL}</a></div>
            <div><span className="eyebrow block mb-1">Instagram</span><a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="underline" data-testid="contact-direct-instagram">@sanoacupuncture</a></div>
            <div><span className="eyebrow block mb-1">Google Reviews</span><a href="https://g.page/r/Cesfk43U40Z9EBM/review" target="_blank" rel="noreferrer" className="underline" data-testid="contact-google-review">Leave a review →</a></div>
          </div>
        </div>

        <form onSubmit={submitContact} className="lg:col-span-6 lg:col-start-7 space-y-5" data-testid="contact-form">
          <div className="eyebrow mb-2">Send a private note</div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name *" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} testid="contact-name" />
            <Field label="Email *" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} testid="contact-email" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} testid="contact-phone" />
            <Field label="Subject" value={contact.subject} onChange={(e) => setContact({ ...contact, subject: e.target.value })} testid="contact-subject" />
          </div>
          <label className="block">
            <span className="eyebrow block mb-2">Your message *</span>
            <textarea
              rows={6}
              value={contact.message}
              onChange={(e) => setContact({ ...contact, message: e.target.value })}
              className="w-full px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm resize-none"
              style={{ borderColor: "var(--sano-border)" }}
              data-testid="contact-message"
            />
          </label>
          <div className="flex flex-wrap items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={loadingC}
              className="px-7 py-3.5 rounded-full text-sm font-medium text-[#F9F6F0] disabled:opacity-50"
              style={{ background: "var(--sano-olive)" }}
              data-testid="contact-submit"
            >
              {loadingC ? "Sending…" : "Send message"}
            </button>
            <a
              href={`mailto:${EMAIL}?subject=${encodeURIComponent("Question for Sano Acupuncture")}`}
              className="inline-flex items-center gap-2 text-sm hover:underline"
              style={{ color: "var(--sano-olive)" }}
              data-testid="contact-mailto"
            >
              <Mail size={14} strokeWidth={1.5} /> Or email {EMAIL} directly
            </a>
          </div>
          <p className="text-xs" style={{ color: "var(--sano-ink-2)" }}>
            On submit, your email app will open with a pre-filled message addressed to {EMAIL}.
          </p>
          <div className="pt-4">
            <p className="text-xs mb-2" style={{ color: "var(--sano-ink-2)" }}>Ready to book instead?</p>
            <BookingButton testid="contact-book-btn" size="sm" />
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="w-full px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm"
        style={{ borderColor: "var(--sano-border)" }}
        data-testid={testid}
      />
    </label>
  );
}
